import {
    Button,
    Checkbox,
    Classes,
    Intent,
    Position,
    Toaster
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { Banner } from "components/common/Banner";
import { NiraDialog } from "components/common/NiraDialog";
import { Surface } from "components/common/Surface";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { AdminOnlyTooltip } from "in_queue/components/common/AdminOnlyTooltip";
import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    CreatedScenario,
    useBulkRerunScenarios
} from "in_queue/hooks/useBulkRerunScenarios";
import { TrackedProject } from "in_queue/types/projectType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { RERUN_VERB } from "in_queue/utils/scenarioSelection";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_TYPE } from "types/roleType";
import css from "./BulkRerunScenariosDialog.module.scss";
import {
    bulkRerunToast,
    droppedWholeProjectOnlyToast,
    scenarioCount
} from "./bulkRerunToast";
import { ScenarioRerunTitle } from "./ScenarioRerunTitle";

const BulkRerunToaster = Toaster.create({
    position: Position.TOP
});

const rerunTitle = (title: string, now: DateTime): string =>
    `${title} (${now.toFormat("yyyy-MM-dd")} re-run)`;

/**
 * Confirms a duplicate-and-re-run by previewing every scenario it is about to
 * create.
 *
 * The action duplicates rather than overwrites, which isn't obvious from the
 * table, so each new name is listed — and editable — before anything is
 * queued. Archiving the originals is offered here so a re-run can replace the
 * scenario it copied instead of sitting beside it.
 */
export const BulkRerunScenariosDialog: React.FC<{
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    scenarios: ReadonlyArray<ScenarioMetadata>;
    project: TrackedProject;
    onRerun: () => void;
}> = ({ isOpen, setIsOpen, scenarios, project, onRerun }) => {
    const { rerunScenarios, isRerunning } = useBulkRerunScenarios({ project });
    const cluster = useCluster();
    const { role } = useUserDataContext();
    const navigate = useNavigate();
    const count = scenarios.length;
    const defaultTitles = useMemo(() => {
        const now = DateTime.now();
        return Object.fromEntries(
            scenarios.map((scenario) => [
                scenario.scenarioId,
                rerunTitle(scenario.title, now)
            ])
        );
    }, [scenarios]);
    const [renamed, setRenamed] = useState<Record<string, string>>({});
    const [archiveOriginals, setArchiveOriginals] = useState(false);
    const willArchive = archiveOriginals && role === ADMIN_TYPE;

    const titleFor = (scenario: ScenarioMetadata) =>
        renamed[scenario.scenarioId] ??
        defaultTitles[scenario.scenarioId] ??
        "";

    const close = () => {
        setRenamed({});
        setArchiveOriginals(false);
        setIsOpen(false);
    };

    const hasBlankTitle = scenarios.some(
        (scenario) => titleFor(scenario).trim() === ""
    );

    const openAction = (created: CreatedScenario) => {
        const href = `/${IN_QUEUE_PATH}/${cluster.slug}/${project.projectId}/scenario/${created.scenarioId}`;
        return {
            text: "Open",
            href,
            onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                event.preventDefault();
                navigate(href);
            }
        };
    };

    const confirm = async () => {
        const { created, archive, droppedWholeProjectOnlyIds } =
            await rerunScenarios(
                scenarios.map((scenario) => ({
                    scenario,
                    title: titleFor(scenario).trim()
                })),
                willArchive
            );
        close();

        if (created.length === 0) {
            BulkRerunToaster.show({
                intent: Intent.DANGER,
                message: "Couldn't re-run scenarios. Please try again."
            });
            return;
        }

        const droppedToast = droppedWholeProjectOnlyToast(
            droppedWholeProjectOnlyIds
        );
        if (droppedToast) {
            BulkRerunToaster.show(droppedToast);
        }

        BulkRerunToaster.show({
            ...bulkRerunToast(created.length, archive),
            ...(created.length === 1 ? { action: openAction(created[0]) } : {})
        });
        onRerun();
    };

    return (
        <NiraDialog
            isOpen={isOpen}
            onClose={close}
            eyebrow="Scenario Management"
            title={RERUN_VERB}
            className={css["BulkRerunScenariosDialog"]}
            gap="large"
            footerActions={
                <>
                    <Button
                        text="Cancel"
                        onClick={close}
                        disabled={isRerunning}
                        size="large"
                    />
                    <Button
                        text={
                            count === 1
                                ? "Duplicate & run"
                                : `Duplicate & run ${count}`
                        }
                        intent={Intent.PRIMARY}
                        loading={isRerunning}
                        disabled={hasBlankTitle}
                        onClick={confirm}
                        size="large"
                    />
                </>
            }
        >
            <Banner
                icon={IconNames.INFO_SIGN}
                intent={Intent.PRIMARY}
                title={`${scenarioCount(count)} will be created`}
                description="Queued against the latest model files."
            />
            <Surface bordered padding="small">
                <ul className={css["BulkRerunScenariosDialog-list"]}>
                    {scenarios.map((scenario) => (
                        <li
                            key={scenario.scenarioId}
                            className={css["BulkRerunScenariosDialog-item"]}
                        >
                            <ScenarioRerunTitle
                                originalTitle={scenario.title}
                                value={titleFor(scenario)}
                                disabled={isRerunning}
                                onChange={(value) =>
                                    setRenamed((previous) => ({
                                        ...previous,
                                        [scenario.scenarioId]: value
                                    }))
                                }
                            />
                        </li>
                    ))}
                </ul>
            </Surface>
            <AdminOnlyTooltip>
                <Checkbox
                    className={css["BulkRerunScenariosDialog-archive"]}
                    checked={willArchive}
                    disabled={role !== ADMIN_TYPE || isRerunning}
                    labelElement={
                        <>
                            {`Archive ${count} original ${
                                count === 1 ? "scenario" : "scenarios"
                            }`}
                            {willArchive && (
                                <span
                                    className={classNames(
                                        css[
                                            "BulkRerunScenariosDialog-archive-help"
                                        ],
                                        Classes.TEXT_MUTED,
                                        Classes.TEXT_SMALL
                                    )}
                                >
                                    Archived scenarios are hidden by default.
                                </span>
                            )}
                        </>
                    }
                    onChange={(event) =>
                        setArchiveOriginals(event.currentTarget.checked)
                    }
                />
            </AdminOnlyTooltip>
        </NiraDialog>
    );
};
